/**
 * Calcoli totali preventivo / review commerciale (client-side).
 * Allineato a CommercialReviewPreviewService + calculateActivityCost legacy.
 */

export const UNIT_MULTIPLIERS = {
    hours: 1,
    days: 8,
    weeks: 40,
    months: 173.33,
};

export function findCost(costs, costId) {
    if (!costId) {
        return null;
    }
    return costs.find((c) => String(c.id) === String(costId)) || null;
}

export function findProduct(products, productId) {
    if (!productId) {
        return null;
    }
    return products.find((p) => String(p.id) === String(productId)) || null;
}

/**
 * Ricalcola ore / costo vivo / costo ore su un'attività.
 * Per overhead: quantity/ore vanno ricalcolati a livello SAL via recalculateSalActivities.
 */
export function recalculateActivity(activity, costs, products, options = {}) {
    const product = findProduct(products, activity.product_id);
    const cost = findCost(costs, activity.cost_id);
    const skipOverheadQuantity = !!options.skipOverheadQuantity;

    if (product && product.type === 'overhead') {
        if (!skipOverheadQuantity && options.salActivities) {
            return applyOverheadCalculation(activity, costs, products, options.salActivities);
        }
        // Solo ricalcolo prezzo da ore già impostate
        const hours = parseFloat(activity.quantity) || 0;
        let totalPrice = 0;
        if (cost && !cost.is_fixed_cost && !cost.is_expense) {
            totalPrice = hours * (parseFloat(cost.cost_per_hour) || 0);
        }
        return {
            ...activity,
            unit: 'hours',
            total_hours: round2(hours),
            total_cost: 0,
            total_price: round2(totalPrice),
        };
    }

    const quantity = parseFloat(activity.quantity) || 0;
    const unit = activity.unit || 'hours';
    const multiplier = UNIT_MULTIPLIERS[unit] || 1;

    let totalHours = 0;
    let totalCost = 0;
    let totalPrice = 0;

    if (!cost) {
        totalHours = 0;
        totalCost = 0;
        totalPrice = 0;
    } else if (cost.is_expense) {
        totalCost = quantity;
    } else if (!cost.is_fixed_cost) {
        totalHours = quantity * multiplier;
        totalPrice = totalHours * (parseFloat(cost.cost_per_hour) || 0);
    } else {
        totalPrice = quantity;
    }

    return {
        ...activity,
        total_hours: round2(totalHours),
        total_cost: round2(totalCost),
        total_price: round2(totalPrice),
    };
}

export function isOverheadActivity(activity, products) {
    const product = findProduct(products, activity?.product_id);
    return !!(product && product.type === 'overhead');
}

/**
 * Ore base SAL (esclude overhead e spese): quantity × multiplier per risorse orarie.
 */
export function sumSalBaseHours(activities, costs, products) {
    let total = 0;
    (activities || []).forEach((activity) => {
        if (isOverheadActivity(activity, products)) {
            return;
        }
        const cost = findCost(costs, activity.cost_id);
        if (!cost || cost.is_fixed_cost || cost.is_expense) {
            return;
        }
        const quantity = parseFloat(activity.quantity) || 0;
        const unit = activity.unit || 'hours';
        const multiplier = UNIT_MULTIPLIERS[unit] || 1;
        total += quantity * multiplier;
    });
    return round2(total);
}

export function applyOverheadCalculation(activity, costs, products, salActivities) {
    const product = findProduct(products, activity.product_id);
    if (!product || product.type !== 'overhead') {
        return recalculateActivity(activity, costs, products, { skipOverheadQuantity: true });
    }

    let next = { ...activity, unit: 'hours' };
    if (!next.cost_id && product.default_cost_id) {
        next.cost_id = product.default_cost_id;
    }

    const baseHours = sumSalBaseHours(salActivities, costs, products);
    const percentage = parseFloat(product.standard_value) || 0;
    const overheadHours = round2(baseHours * (percentage / 100));
    next.quantity = overheadHours;

    const cost = findCost(costs, next.cost_id);
    let totalPrice = 0;
    if (cost && !cost.is_fixed_cost && !cost.is_expense) {
        totalPrice = overheadHours * (parseFloat(cost.cost_per_hour) || 0);
    }

    next.total_hours = overheadHours;
    next.total_cost = 0;
    next.total_price = round2(totalPrice);
    return next;
}

/**
 * Ricalcola tutte le attività di un SAL (prima non-overhead, poi overhead).
 */
export function recalculateSalActivities(activities, costs, products) {
    const list = (activities || []).map((activity) => ({ ...activity }));
    list.forEach((activity, index) => {
        if (isOverheadActivity(activity, products)) {
            return;
        }
        list[index] = recalculateActivity(activity, costs, products, { skipOverheadQuantity: true });
    });
    list.forEach((activity, index) => {
        if (!isOverheadActivity(activity, products)) {
            return;
        }
        list[index] = applyOverheadCalculation(activity, costs, products, list);
    });
    return list;
}

export function activityNetRevenue(activity) {
    const revenue = parseFloat(activity.revenue) || 0;
    const discount = parseFloat(activity.line_discount) || 0;
    return Math.max(0, revenue - discount);
}

export function rawCostPerHour(cost) {
    if (!cost) {
        return 0;
    }
    return parseFloat(cost.cost_per_hour_raw != null ? cost.cost_per_hour_raw : cost.cost_per_hour) || 0;
}

/**
 * Costo effettivo riga: ore × tariffa risorsa grezza + costi vivi.
 */
export function actualLineCost(activity, costs) {
    const cost = findCost(costs, activity?.cost_id);
    const hours = parseFloat(activity?.total_hours) || 0;
    const live = parseFloat(activity?.total_cost) || 0;
    if (!cost) {
        return round2(live + (parseFloat(activity?.total_price) || 0));
    }
    if (cost.is_expense) {
        return round2(live || parseFloat(activity?.quantity) || 0);
    }

    return round2((hours * rawCostPerHour(cost)) + live);
}

/**
 * Somma i costi effettivi del blocco multi-risorsa (o della riga singola).
 */
export function groupActualCost(activities, costs, activity) {
    const gid = activityGroupId(activity);
    const rows = gid
        ? (activities || []).filter((row) => activityGroupId(row) === gid)
        : [activity];
    return round2(rows.reduce((sum, row) => sum + actualLineCost(row, costs), 0));
}

/**
 * GM% sul prezzo netto. null se netto = 0.
 */
export function lineMarginPercent(netRevenue, actualCost) {
    const net = Number(netRevenue) || 0;
    if (net <= 0) {
        return null;
    }
    return round2(((net - (Number(actualCost) || 0)) / net) * 100);
}

/**
 * Tono UI per GM%: soglie commerciali, non “verde se ≥ 0”.
 * @returns {'muted'|'pure'|'bad'|'warn'|'ok'}
 */
export function gmTone(gmPercent, actualCost, netRevenue) {
    if (gmPercent === null || gmPercent === undefined || Number.isNaN(Number(gmPercent))) {
        return 'muted';
    }
    const net = Number(netRevenue) || 0;
    const cost = Number(actualCost) || 0;
    if (cost === 0 && net > 0) {
        return 'pure';
    }
    const gm = Number(gmPercent);
    if (gm < 15) {
        return 'bad';
    }
    if (gm < 30) {
        return 'warn';
    }
    return 'ok';
}

export function gmToneClass(tone) {
    return 'qce-gm--' + (tone || 'muted');
}

export function activityGroupId(activity) {
    return String(activity?.activity_group_id || '').trim();
}

/**
 * Raggruppa le attività in blocchi drag (primary + continuation stesso group_id).
 * @param {Array} activities
 * @returns {Array<{key: string, activities: Array}>}
 */
export function activitiesToBlocks(activities) {
    const list = activities || [];
    const blocks = [];
    const seenGroups = new Set();

    list.forEach((activity, index) => {
        const gid = activityGroupId(activity);
        if (gid) {
            if (seenGroups.has(gid)) {
                return;
            }
            seenGroups.add(gid);
            blocks.push({
                key: 'g-' + gid,
                activities: list.filter((row) => activityGroupId(row) === gid),
            });
            return;
        }
        blocks.push({
            key: activity.client_key || ('solo-' + index),
            activities: [activity],
        });
    });

    return blocks;
}

/**
 * @param {Array<{activities: Array}>} blocks
 * @returns {Array}
 */
export function blocksToActivities(blocks) {
    const out = [];
    (blocks || []).forEach((block) => {
        (block.activities || []).forEach((activity) => {
            out.push(activity);
        });
    });
    return out;
}

/**
 * Prima riga del gruppo (o riga singola senza group id).
 */
export function isActivityGroupPrimary(activities, index) {
    const list = activities || [];
    const activity = list[index];
    if (!activity) {
        return false;
    }
    const gid = activityGroupId(activity);
    if (!gid) {
        return true;
    }
    return list.findIndex((row) => activityGroupId(row) === gid) === index;
}

export function isSalExcluded(sal) {
    return !!(sal.is_optional || sal.is_recurring);
}

export function isActivityExcluded(activity, sal) {
    if (isSalExcluded(sal)) {
        return true;
    }
    return !!activity.is_optional;
}

/**
 * @returns {{ hours: number, hoursCost: number, liveCost: number, actualCost: number, revenue: number, headerDiscount: number, discountedRevenue: number, margin: number, gmPercent: number|null, totalCost: number }}
 */
export function computeQuoteTotals(sals, costs, headerDiscount) {
    let hours = 0;
    let hoursCost = 0;
    let liveCost = 0;
    let actualCost = 0;
    let revenue = 0;
    const costsList = costs || [];

    (sals || []).forEach((sal) => {
        (sal.activities || []).forEach((activity, index) => {
            if (isActivityExcluded(activity, sal)) {
                return;
            }
            hours += parseFloat(activity.total_hours) || 0;
            hoursCost += parseFloat(activity.total_price) || 0;
            liveCost += parseFloat(activity.total_cost) || 0;
            actualCost += actualLineCost(activity, costsList);
            // Ricavo unificato: conta una sola volta per activity_group
            if (isActivityGroupPrimary(sal.activities, index)) {
                revenue += activityNetRevenue(activity);
            }
        });
    });

    hours = round2(hours);
    hoursCost = round2(hoursCost);
    liveCost = round2(liveCost);
    actualCost = round2(actualCost);
    revenue = round2(revenue);
    const discount = Math.max(0, parseFloat(headerDiscount) || 0);
    const discountedRevenue = round2(Math.max(0, revenue - discount));
    const totalCost = round2(hoursCost + liveCost);
    const margin = round2(discountedRevenue - actualCost);
    const gmPercent = lineMarginPercent(discountedRevenue, actualCost);

    return {
        hours,
        hoursCost,
        liveCost,
        actualCost,
        revenue,
        headerDiscount: round2(discount),
        discountedRevenue,
        margin,
        gmPercent,
        totalCost,
    };
}

function parseQuoteDateParts(value) {
    if (!value) {
        return null;
    }
    const trimmed = String(value).trim();
    const dmy = trimmed.split('/');
    if (dmy.length === 3) {
        const day = parseInt(dmy[0], 10);
        const month = parseInt(dmy[1], 10) - 1;
        const year = parseInt(dmy[2], 10);
        if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year)) {
            return { year, month };
        }
    }
    const iso = Date.parse(trimmed);
    if (!Number.isNaN(iso)) {
        const dt = new Date(iso);
        return { year: dt.getFullYear(), month: dt.getMonth() };
    }
    return null;
}

export function recurringPeriodCount(activity, sal) {
    const startVal = activity?.recurring_start_date || sal?.recurring_start_date;
    const endVal = activity?.recurring_end_date || sal?.recurring_end_date;
    const period = activity?.recurring_period || sal?.recurring_period;
    if (!startVal || !endVal || !period) {
        return 1;
    }
    const start = parseQuoteDateParts(startVal);
    const end = parseQuoteDateParts(endVal);
    if (!start || !end) {
        return 1;
    }
    const months = (end.year - start.year) * 12 + (end.month - start.month) + 1;
    if (months < 1) {
        return 1;
    }
    switch (period) {
        case 'monthly':
            return months;
        case 'quarterly':
            return Math.max(1, Math.ceil(months / 3));
        case 'semi-annual':
            return Math.max(1, Math.ceil(months / 6));
        case 'annual':
            return Math.max(1, (end.year - start.year) + 1);
        default:
            return 1;
    }
}

export function applySalRecurringToActivities(sal) {
    const apply = (row) => {
        if (!row) {
            return;
        }
        row.recurring_period = sal.recurring_period || '';
        row.recurring_start_date = sal.recurring_start_date || '';
        row.recurring_end_date = sal.recurring_end_date || '';
        row.recurring_advance = !!sal.recurring_advance;
    };
    (sal.activities || []).forEach(apply);
    (sal.activityBlocks || []).forEach((block) => {
        (block.rows || []).forEach(apply);
    });
    return sal;
}

/**
 * Totali sticky SAL ricorrenti a valore contratto.
 * In edit Vue ore/costi/ricavi sono per periodo (recalculateActivity non moltiplica):
 * tutti i KPI vanno × n. periodi. In DB quote i total_* sono già × periodi (save PHP).
 */
export function computeRecurringQuoteTotals(sals, costs) {
    let hours = 0;
    let hoursCost = 0;
    let liveCost = 0;
    let actualCost = 0;
    let revenue = 0;
    let lineDiscount = 0;
    const costsList = costs || [];

    (sals || []).forEach((sal) => {
        if (!sal.is_recurring || sal.is_optional) {
            return;
        }
        (sal.activities || []).forEach((activity, index) => {
            if (activity.is_optional) {
                return;
            }
            const periods = recurringPeriodCount(activity, sal);
            hours += (parseFloat(activity.total_hours) || 0) * periods;
            hoursCost += (parseFloat(activity.total_price) || 0) * periods;
            liveCost += (parseFloat(activity.total_cost) || 0) * periods;
            actualCost += actualLineCost(activity, costsList) * periods;
            if (isActivityGroupPrimary(sal.activities, index)) {
                revenue += (parseFloat(activity.revenue) || 0) * periods;
                lineDiscount += (parseFloat(activity.line_discount) || 0) * periods;
            }
        });
    });

    hours = round2(hours);
    hoursCost = round2(hoursCost);
    liveCost = round2(liveCost);
    actualCost = round2(actualCost);
    revenue = round2(revenue);
    lineDiscount = round2(lineDiscount);
    const discountedRevenue = round2(Math.max(0, revenue - lineDiscount));
    const totalCost = round2(hoursCost + liveCost);
    const margin = round2(discountedRevenue - actualCost);
    const gmPercent = lineMarginPercent(discountedRevenue, actualCost);

    return {
        hours,
        hoursCost,
        liveCost,
        actualCost,
        revenue,
        lineDiscount,
        discountedRevenue,
        margin,
        gmPercent,
        totalCost,
    };
}

/**
 * Totali di un SAL (allineati alle righe, per periodo se ricorrente).
 * Se il SAL è opzionale include tutte le righe: il footer non deve andare a zero.
 * Su SAL non opzionale le attività is_optional restano escluse (come i KPI globali).
 *
 * @param {object} options
 * @param {boolean} [options.scaleByPeriods] valore contratto (× n. periodi per riga)
 */
export function computeSalTotals(sal, costs, options = {}) {
    let hours = 0;
    let hoursCost = 0;
    let liveCost = 0;
    let actualCost = 0;
    let revenue = 0;
    let lineDiscount = 0;
    const costsList = costs || [];
    const includeOptional = options.includeOptional != null
        ? !!options.includeOptional
        : !!sal.is_optional;
    const scaleByPeriods = !!options.scaleByPeriods;

    (sal.activities || []).forEach((activity, index) => {
        if (activity.is_optional && !includeOptional) {
            return;
        }
        const periods = scaleByPeriods ? recurringPeriodCount(activity, sal) : 1;
        hours += (parseFloat(activity.total_hours) || 0) * periods;
        hoursCost += (parseFloat(activity.total_price) || 0) * periods;
        liveCost += (parseFloat(activity.total_cost) || 0) * periods;
        actualCost += actualLineCost(activity, costsList) * periods;
        if (isActivityGroupPrimary(sal.activities, index)) {
            revenue += (parseFloat(activity.revenue) || 0) * periods;
            lineDiscount += (parseFloat(activity.line_discount) || 0) * periods;
        }
    });

    hours = round2(hours);
    hoursCost = round2(hoursCost);
    liveCost = round2(liveCost);
    actualCost = round2(actualCost);
    revenue = round2(revenue);
    lineDiscount = round2(lineDiscount);
    const discountedRevenue = round2(Math.max(0, revenue - lineDiscount));

    return {
        hours,
        hoursCost,
        liveCost,
        actualCost,
        revenue,
        lineDiscount,
        discountedRevenue,
        gmPercent: lineMarginPercent(discountedRevenue, actualCost),
    };
}

export function recurringPeriodLabel(activity, sal) {
    const count = recurringPeriodCount(activity, sal);
    const period = activity?.recurring_period || sal?.recurring_period;
    const units = {
        monthly: count === 1 ? 'mese' : 'mesi',
        quarterly: count === 1 ? 'trimestre' : 'trimestri',
        'semi-annual': count === 1 ? 'semestre' : 'semestri',
        annual: count === 1 ? 'anno' : 'anni',
    };
    const unit = units[period] || (count === 1 ? 'periodo' : 'periodi');
    return count + ' ' + unit;
}

export function maxRecurringPeriodCount(sals) {
    let max = 0;
    (sals || []).forEach((sal) => {
        if (!sal.is_recurring || sal.is_optional) {
            return;
        }
        (sal.activities || []).forEach((activity) => {
            if (activity.is_optional) {
                return;
            }
            max = Math.max(max, recurringPeriodCount(activity, sal));
        });
    });
    return max;
}

export function sumSalsNetRevenue(sals) {
    let revenue = 0;
    (sals || []).forEach((sal) => {
        if (sal.is_optional) {
            return;
        }
        (sal.activities || []).forEach((activity, index) => {
            if (activity.is_optional) {
                return;
            }
            if (isActivityGroupPrimary(sal.activities, index)) {
                revenue += activityNetRevenue(activity);
            }
        });
    });
    return round2(revenue);
}

export function previewPricingDelta(sals, costs, options) {
    const before = sumSalsNetRevenue(sals);
    const clone = JSON.parse(JSON.stringify(sals || []));
    applyPricingToRevenues(clone, costs, options);
    const after = sumSalsNetRevenue(clone);
    return {
        before,
        after,
        delta: round2(after - before),
    };
}

export function formatMoney(value) {
    const n = Number(value) || 0;
    return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatHours(value) {
    const n = Number(value) || 0;
    const decimals = Math.abs(n % 1) > 0.0001 ? 2 : 0;
    return n.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: 2 });
}

export function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

/** Arrotonda al multiplo di 50 € più vicino. */
export function roundToNearestFifty(n) {
    return Math.round((Number(n) || 0) / 50) * 50;
}

/**
 * Ratecard interpolata (allineata a Cost::getRatecardForPercentage).
 */
export function resolveRatecard(cost, ratecardPercentage, ratecardRangeEnabled) {
    if (!cost) {
        return 0;
    }
    const min = cost.ratecard_min;
    const max = cost.ratecard_max;
    if (
        ratecardRangeEnabled
        && ratecardPercentage !== null
        && ratecardPercentage !== undefined
        && min !== null
        && min !== undefined
        && max !== null
        && max !== undefined
    ) {
        return round2(Number(min) + (Number(max) - Number(min)) * (Number(ratecardPercentage) / 100));
    }
    return Number(cost.ratecard) || 0;
}

/**
 * Ricavo di una singola riga (allineato a PricingCalculationService::calculateLineRevenueFromCostMeta).
 * I prezzi calcolati sono arrotondati ai 50 €.
 */
export function calculateLineRevenue(activity, cost, options) {
    const mode = options.mode || 'markup';
    const markup = Number(options.markup) || 0;
    const ratecardPercentage = options.ratecardPercentage;
    const ratecardRangeEnabled = !!options.ratecardRangeEnabled;
    const totalHours = parseFloat(activity.total_hours) || 0;
    const totalCost = parseFloat(activity.total_cost) || 0;
    const currentRevenue = parseFloat(activity.revenue) || 0;

    const isFixed = !!(cost && cost.is_fixed_cost);
    const isExpense = !!(cost && cost.is_expense);

    if (isFixed && !isExpense && totalCost === 0) {
        return round2(currentRevenue);
    }

    if (mode === 'ratecard') {
        if (cost && !isFixed && totalHours > 0) {
            const rate = resolveRatecard(cost, ratecardPercentage, ratecardRangeEnabled);
            return roundToNearestFifty(rate * totalHours);
        }
        return roundToNearestFifty(totalCost * (1 + markup / 100));
    }

    if (cost && !isFixed && totalHours > 0) {
        const costPerHour = parseFloat(cost.cost_per_hour_raw != null ? cost.cost_per_hour_raw : cost.cost_per_hour) || 0;
        return roundToNearestFifty(totalHours * costPerHour * (1 + markup / 100));
    }

    return roundToNearestFifty(totalCost * (1 + markup / 100));
}

/**
 * Applica markup/ratecard a tutti i ricavi (mutazione in-place delle attività).
 * Gruppi multi-risorsa: somma sulla primaria, continuation = 0.
 * Salta SAL/attività opzionali.
 */
export function applyPricingToRevenues(sals, costs, options) {
    const costsList = costs || [];
    (sals || []).forEach((sal) => {
        if (sal.is_optional) {
            return;
        }
        const activities = sal.activities || [];
        const groupRevenues = {};

        activities.forEach((activity, index) => {
            if (activity.is_optional) {
                return;
            }
            const cost = findCost(costsList, activity.cost_id);
            const line = calculateLineRevenue(activity, cost, options);
            const gid = activityGroupId(activity);
            if (!gid) {
                activity.revenue = line;
                return;
            }
            if (!groupRevenues[gid]) {
                groupRevenues[gid] = 0;
            }
            groupRevenues[gid] = round2(groupRevenues[gid] + line);
            if (!isActivityGroupPrimary(activities, index)) {
                activity.revenue = 0;
            }
        });

        Object.keys(groupRevenues).forEach((gid) => {
            const primaryIndex = activities.findIndex((row) => activityGroupId(row) === gid);
            if (primaryIndex >= 0) {
                activities[primaryIndex].revenue = roundToNearestFifty(groupRevenues[gid]);
            }
        });
    });
    return sals;
}

export function newClientKey(prefix) {
    return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
}
